import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { EditorState, RichUtils, AtomicBlockUtils } from 'draft-js';
import Editor, { composeDecorators } from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import createResizeablePlugin from 'draft-js-resizeable-plugin';
import createFocusPlugin from 'draft-js-focus-plugin';
import isSoftNewlineEvent from 'draft-js/lib/isSoftNewlineEvent';
import { validateIFrame } from 'lib/iframe-validator';
import { Button, Form, Icon, Input, Label, Modal, Dropdown } from 'semantic-ui-react';
import config from 'config';

import 'draft-js/dist/Draft.css';
import styles from './styles.css';
import locale from './locale.js';

const focusPlugin = createFocusPlugin();
const resizeablePlugin = createResizeablePlugin({
  vertical: 'relative',
  horizontal: 'absolute',
});

const decorator = composeDecorators(resizeablePlugin.decorator, focusPlugin.decorator);

const imagePlugin = createImagePlugin({ decorator });

const VideoElement = props => {
  // getEntity errors out when props.block.getEntityAt(0)
  // returns null. So, returning null to supress the erro.
  if(!props.block.getEntityAt(0)) return null;

  const entity = props.contentState.getEntity(props.block.getEntityAt(0));
  const { code } = entity.getData();
  // Code is sanitized when added, only safe iframes here
  return <div dangerouslySetInnerHTML={{ __html: code }} />; // eslint-disable-line react/no-danger
};

class MyEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      linkModalOpen: false,
      imageModalOpen: false,
      imageModalLoading: false,
      imageError: false,
      proposedLink: '',
      videoModalOpen: false,
      proposedVideo: '',
      videoError: false,
      showVideo: Object.prototype.hasOwnProperty.call(this.props, 'showVideo') ? this.props.showVideo : true,
      showImage: Object.prototype.hasOwnProperty.call(this.props, 'showImage') ? this.props.showImage : true,
      showHeading: Object.prototype.hasOwnProperty.call(this.props, 'showHeading') ? this.props.showHeading : true,
    };

    this.onChange = editorState => {
      this.props.onEditorStateChange(editorState);
    };

    this.uploadFile = this.uploadFile.bind(this);
    this.blockRenderer = this.blockRenderer.bind(this);
    this.validateImageSize = this.validateImageSize.bind(this);

    this.fileRef = React.createRef();
  }

  validateImageSize = () => {
    return (this.fileRef.current.files[0].size / 1024 / 1024) <= 2; // file size in MB
  };


  blockRenderer = contentBlock => {
    const type = contentBlock.getType();
    if (type === 'atomic') {
      return {
        component: VideoElement,
        editable: false,
      };
    }
    return undefined;
  };

  uploadFile = () => {
    this.setState({
      imageModalLoading: true,
      imageError: false,
    });
    const formData = new FormData();
    formData.append('file', this.fileRef.current.files[0], this.fileRef.current.files[0].name);

    const img = new Image();
    img.onload = () => {
      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      window.URL.revokeObjectURL(img.src);

      fetch(`${config.api.host}/attachment?key=filelink`, {
        method: 'POST',
        body: formData,
      })
        .then(response => response.json())
        .then(data => {
          const newState = imagePlugin.addImage(this.props.editorState, data.filelink, {
            width: originalWidth,
            height: originalHeight,
            originalWidth,
            originalHeight,
          });
          this.setState({
            imageModalOpen: false,
            imageModalLoading: false,
          });
          this.props.onEditorStateChange(newState);
        })
        .catch(a => {
          console.warn(a); // eslint-disable-line no-console
          this.setState({ imageModalLoading: false });
        });
    };
    img.src = window.URL.createObjectURL(this.fileRef.current.files[0]);
  };

  render() {
    return (
      <React.Fragment>
        <Modal
          open={this.state.videoModalOpen}
          size="mini"
          onClose={() =>
            this.setState({
              videoModalOpen: false,
              proposedVideo: '',
              videoError: false,
            })
          }
          closeIcon
        >
          <Modal.Header htmlFor="modal-video-url">{this.props.intl.formatMessage(locale.enterVideo)}</Modal.Header>
          <Modal.Content>
            <Form>
              <Form.Field error={this.state.videoError}>
                <Input
                  fluid
                  id="modal-video-url"
                  onChange={(event, { value }) => {
                    event.preventDefault();
                    this.setState({
                      proposedVideo: value,
                      videoError: false,
                    });
                  }}
                  placeholder={this.props.intl.formatMessage(locale.pasteVideo)}
                />
                {this.state.videoError && (
                  <Label basic color="red">
                    <Icon className="universe-exclamation" />
                    {this.props.intl.formatMessage(locale.videoError)}
                  </Label>
                )}
              </Form.Field>
            </Form>
          </Modal.Content>
          {this.state.showVideo && (
            <Modal.Actions>
              <Button
                primary
                onClick={e => {
                  e.preventDefault();
                  this.editor.focus();
                  const { editorState } = this.props;
                  const { proposedVideo } = this.state;

                  validateIFrame(proposedVideo)
                    .then(sanitized => {
                      const contentState = editorState.getCurrentContent();
                      const contentStateWithVideo = contentState.createEntity('VIDEO', 'IMMUTABLE', {
                        code: sanitized,
                      });
                      const entityKey = contentStateWithVideo.getLastCreatedEntityKey();
                      const newEditorState = EditorState.set(
                        editorState,
                        { currentContent: contentStateWithVideo },
                        'create-entity',
                      );
                      this.setState(
                        {
                          proposedVideo: '',
                          videoModalOpen: false,
                        },
                        () => {
                          setTimeout(() => this.editor.focus(), 0);
                        },
                      );

                      this.props.onEditorStateChange(
                        AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' '),
                      );
                    })
                    .catch(() => this.setState({ videoError: true }));
                }}
              >
                {this.props.intl.formatMessage(locale.embedVideo)}
              </Button>
            </Modal.Actions>
          )}
        </Modal>
        <Modal
          open={this.state.linkModalOpen}
          size="mini"
          onClose={() =>
            this.setState({
              linkModalOpen: false,
              proposedLink: '',
            })
          }
          closeIcon
        >
          <Modal.Header>{this.props.intl.formatMessage(locale.enterLink)}</Modal.Header>
          <Modal.Content>
            <Input
              fluid
              id="modal-link-url"
              onChange={(event, { value }) => {
                event.preventDefault();
                this.setState({ proposedLink: value });
              }}
              placeholder="Link"
            />
          </Modal.Content>
          <Modal.Actions>
            <Button
              primary
              onClick={e => {
                e.preventDefault();
                const { editorState } = this.props;
                const { proposedLink } = this.state;

                const contentState = editorState.getCurrentContent();
                const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url: proposedLink });
                const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
                const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });

                this.setState(
                  {
                    proposedLink: '',
                    linkModalOpen: false,
                  },
                  () => {
                    setTimeout(() => this.editor.focus(), 0);
                  },
                );

                this.props.onEditorStateChange(
                  RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey),
                );
              }}
            >
              {this.props.intl.formatMessage(locale.applyLink)}
            </Button>
          </Modal.Actions>
        </Modal>
        <Modal
          open={this.state.imageModalOpen}
          size="mini"
          onClose={() =>
            this.setState({
              imageModalOpen: false,
            })
          }
          closeIcon
        >
          <Modal.Header>{this.props.intl.formatMessage(locale.selectFile)}</Modal.Header>
          <Modal.Content>
            <label>{this.props.intl.formatMessage(locale.maxUploadFileSize)}</label>
            <Form loading={this.state.imageModalLoading}>
              <Form.Field error={this.state.imageError}>
                <input id="file-input" type="file" ref={this.fileRef} />
                {this.state.imageError && (
                  <Label basic color="red">
                    <Icon className="universe-exclamation" />
                    {this.props.intl.formatMessage(locale.imageError)}
                  </Label>
                )}
              </Form.Field>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              primary
              onClick={e => {
                  e.preventDefault();
                  this.validateImageSize() ? this.uploadFile() : this.setState({ imageError: true });
                }
              }
              disabled={this.state.imageModalLoading}
            >
              {this.props.intl.formatMessage(locale.upload)}
            </Button>
          </Modal.Actions>
        </Modal>
        <div className={styles.toolbar}>
          {this.state.showHeading
            ? <Dropdown
                value=""
                trigger={<button className={`${styles.toolbarButton} ${styles.formatting}`} />}
                icon={null}
                options={[
                  { text: this.props.intl.formatMessage(locale.headerOne), value: 1 },
                  { text: this.props.intl.formatMessage(locale.headerTwo), value: 2 },
                  { text: this.props.intl.formatMessage(locale.headerThree), value: 3 },
                  { text: this.props.intl.formatMessage(locale.codeBlock), value: 4 },
                ]}
                selectOnNavigation={false}
                selectOnBlur={false}
                openOnFocus={false}
                onChange={(e, { value }) => {
                  let blockType;
                  switch (value) {
                    case 1:
                      blockType = 'header-one';
                      break;
                    case 2:
                      blockType = 'header-two';
                      break;
                    case 3:
                      blockType = 'header-three';
                      break;
                    case 4:
                      blockType = 'code';
                      break;
                    default:
                      blockType = undefined;
                  }
                  this.props.onEditorStateChange(RichUtils.toggleBlockType(this.props.editorState, blockType));
                  e.preventDefault();
                }}
              />
            : null
          }
          <button
            className={`${styles.toolbarButton} ${styles.bold}`}
            onMouseDown={event => {
              event.preventDefault();
              this.props.onEditorStateChange(RichUtils.toggleInlineStyle(this.props.editorState, 'BOLD'));
            }}
          />
          <button
            className={`${styles.toolbarButton} ${styles.italic}`}
            onMouseDown={event => {
              event.preventDefault();
              this.props.onEditorStateChange(RichUtils.toggleInlineStyle(this.props.editorState, 'ITALIC'));
            }}
          />
          <button
            className={`${styles.toolbarButton} ${styles.strike}`}
            onMouseDown={event => {
              event.preventDefault();
              this.props.onEditorStateChange(RichUtils.toggleInlineStyle(this.props.editorState, 'STRIKETHROUGH'));
            }}
          />
          <button
            className={`${styles.toolbarButton} ${styles.ulist}`}
            onMouseDown={event => {
              event.preventDefault();
              this.props.onEditorStateChange(RichUtils.toggleBlockType(this.props.editorState, 'unordered-list-item'));
            }}
          />
          <button
            className={`${styles.toolbarButton} ${styles.olist}`}
            onMouseDown={event => {
              event.preventDefault();
              this.props.onEditorStateChange(RichUtils.toggleBlockType(this.props.editorState, 'ordered-list-item'));
            }}
          />
          <button
            className={`${styles.toolbarButton} ${styles.link}`}
            onMouseDown={event => {
              event.preventDefault();

              this.setState({
                linkModalOpen: !this.props.editorState.getSelection().isCollapsed(),
              });
            }}
          />
          {this.state.showImage 
            ? <button
                className={`${styles.toolbarButton} ${styles.image}`}
                onMouseDown={event => {
                  event.preventDefault();
                  this.setState({
                    imageModalOpen: !this.state.imageModalOpen,
                  });
                }}
              />
            : null 
          }
          {this.state.showVideo
            ? <button
                className={`${styles.toolbarButton} ${styles.video}`}
                onMouseDown={event => {
                  event.preventDefault();
                  this.setState({
                    videoModalOpen: !this.state.videoModalOpen,
                  });
                }}
              />
            : null
          }
        </div>
        <div className={`editor-wrapper ${this.props.error ? 'error' : ''}`}>
          <Editor
            className="error"
            ref={editor => {
              this.editor = editor;
            }}
            placeholder={this.props.placeholder}
            plugins={[imagePlugin, resizeablePlugin, focusPlugin]}
            editorState={this.props.editorState}
            onChange={this.onChange}
            customStyleMap={{
              STRIKETHROUGH: {
                textDecoration: 'line-through',
              },
            }}
            blockStyleFn={contentBlock => {
              const type = contentBlock.getType();
              if (type === 'code') {
                return styles.blockQuote;
              }
              return undefined;
            }}
            blockRendererFn={this.blockRenderer}
            handleKeyCommand={(command, editorState) => {
              const newState = RichUtils.handleKeyCommand(editorState, command);
              if (newState) {
                this.onChange(newState);
                return 'handled';
              }
              return 'not-handled';
            }}
            handleReturn={(e, editorState) => {
              if (isSoftNewlineEvent(e)) {
                this.onChange(RichUtils.insertSoftNewline(editorState));
                return 'handled';
              }
              return 'not-handled';
            }}
            onTab={event => {
              const newState = RichUtils.onTab(event, this.props.editorState, 4);
              if (newState) {
                this.onChange(newState);
              }
            }}
          />
        </div>
      </React.Fragment>
    );
  }
}

export default injectIntl(MyEditor);
